import { memo } from 'react';
import { Badge, Card } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Monitor, Smartphone } from 'lucide-react';

import { getRelativeTime } from '../../utils/formatRelativeTime';

const deviceIcon = (device) => (/iphone|android|ipad|phone/i.test(device ?? '') ? Smartphone : Monitor);

/**
 * Active sessions of the member: device, a Current badge on the one in use, and
 * "browser · place · when" (the same short time as the Members list).
 *
 * @param {object} props
 * @param {{ id: string, device: string, browser: string, location: string, lastSeenAt: string|null, isCurrent: boolean }[]} props.sessions
 */
function MemberSessionsCard({ sessions }) {
  const { t } = useTranslation('people-access');

  return (
    <Card className="flex flex-col" data-testid="member-sessions-card">
      <h3 className="flex items-center gap-2 border-b border-edge px-4 py-3 text-sm font-semibold text-content">
        <Monitor className="h-4 w-4 text-content-muted" aria-hidden="true" />
        {t('memberTabs.security.sessions.title')}
        <Badge variant="neutral">{sessions.length}</Badge>
      </h3>
      {sessions.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-content-muted">{t('memberTabs.security.sessions.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-3 px-4 py-3">
          {sessions.map((session) => {
            const Icon = deviceIcon(session.device);
            const relative = getRelativeTime(session.lastSeenAt);
            const when = relative ? t(`members.relative.${relative.key}`, { count: relative.count }) : '';
            return (
              <li key={session.id} className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-content-muted">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="flex items-center gap-2 text-sm font-medium text-content">
                    {session.device}
                    {session.isCurrent ? <Badge variant="success">{t('memberTabs.security.sessions.current')}</Badge> : null}
                  </span>
                  <span className="text-xs text-content-muted">
                    {t('memberTabs.security.sessions.detail', {
                      browser: session.browser,
                      location: session.location,
                      when,
                    })}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export default memo(MemberSessionsCard);
